export type LastVisitedSpacesCache = Record<string, Record<string, string>>;

const LAST_VISITED_SPACE_STORAGE_KEY = 'lastVisitedSpace';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function getLastVisitedSpacesCache(): LastVisitedSpacesCache {
  try {
    const storedCache = localStorage.getItem(LAST_VISITED_SPACE_STORAGE_KEY);
    if (!storedCache) return {};

    const parsedCache = JSON.parse(storedCache);
    if (!isRecord(parsedCache)) return {};

    const cache: LastVisitedSpacesCache = {};

    Object.entries(parsedCache).forEach(([userId, orgCache]) => {
      if (!isRecord(orgCache)) return;

      const spacesByOrg: Record<string, string> = {};

      Object.entries(orgCache).forEach(([orgId, spaceExternalId]) => {
        if (typeof spaceExternalId === 'string') {
          spacesByOrg[orgId] = spaceExternalId;
        }
      });

      cache[userId] = spacesByOrg;
    });

    return cache;
  } catch {
    return {};
  }
}

export function setLastVisitedSpacesCache(cache: LastVisitedSpacesCache) {
  try {
    localStorage.setItem(LAST_VISITED_SPACE_STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // Best-effort persistence; callers should continue with the in-memory state.
  }
}

export function updateLastVisitedSpace(
  userId: string,
  orgId: string,
  spaceExternalId: string,
) {
  const cache = getLastVisitedSpacesCache();

  setLastVisitedSpacesCache({
    ...cache,
    [userId]: {
      ...cache[userId],
      [orgId]: spaceExternalId,
    },
  });
}

export function clearLastVisitedSpace(userId: string, orgId: string) {
  const cache = getLastVisitedSpacesCache();
  const spacesByOrg = cache[userId];
  if (!spacesByOrg?.[orgId]) return;

  const nextSpacesByOrg = { ...spacesByOrg };
  delete nextSpacesByOrg[orgId];

  const nextCache = { ...cache };
  if (Object.keys(nextSpacesByOrg).length > 0) {
    nextCache[userId] = nextSpacesByOrg;
  } else {
    delete nextCache[userId];
  }

  setLastVisitedSpacesCache(nextCache);
}

export function getLastVisitedSpace(userId: string, orgId: string) {
  return getLastVisitedSpacesCache()[userId]?.[orgId];
}
